// ============================================================
//  Grade 2 Place Value Curriculum
//  Migrated and validated from V5
// ============================================================

export type Difficulty = 'easy' | 'medium' | 'hard';

export interface PlaceValueQuestion {
  type: 'build' | 'identify' | 'compare' | 'repair' | 'path';
  target: number;
  hundreds: number;
  tens: number;
  ones: number;
  // For 'repair': one component has a deliberate error
  repairField?: 'hundreds' | 'tens' | 'ones';
  repairCorrect?: number;
  repairWrong?: number;
  choices?: number[];
  // For 'path'
  pathStart?: number;
  pathEnd?: number;
  pathSteps?: Array<{ op: '+' | '-'; val: 10 | 1 }>;
  hint?: string;
  skillId: string;
}

function decompose(n: number) {
  const hundreds = Math.floor(n / 100);
  const tens = Math.floor((n % 100) / 10);
  const ones = n % 10;
  return { hundreds, tens, ones };
}

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function wrongChoices(correct: number, count: number, min = 0, max = 9): number[] {
  const choices = new Set<number>([correct]);
  let tries = 0;
  while (choices.size < count + 1 && tries < 100) {
    const delta = randomBetween(1, 3) * (Math.random() < 0.5 ? 1 : -1);
    const candidate = Math.max(min, Math.min(max, correct + delta));
    choices.add(candidate);
    tries++;
  }
  return shuffle([...choices]);
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ── Build question ───────────────────────────────────────────
export function makeBuildQuestion(difficulty: Difficulty): PlaceValueQuestion {
  let target: number;
  if (difficulty === 'easy') target = randomBetween(100, 199);
  else if (difficulty === 'medium') target = randomBetween(100, 399);
  else target = randomBetween(100, 999);

  const { hundreds, tens, ones } = decompose(target);
  return {
    type: 'build',
    target, hundreds, tens, ones,
    hint: `Think: How many hundreds? How many tens? How many ones?`,
    skillId: 'math-place-value-build',
  };
}

// ── Repair question ──────────────────────────────────────────
export function makeRepairQuestion(difficulty: Difficulty): PlaceValueQuestion {
  let target: number;
  if (difficulty === 'easy') target = randomBetween(100, 299);
  else if (difficulty === 'medium') target = randomBetween(200, 599);
  else target = randomBetween(200, 999);

  const { hundreds, tens, ones } = decompose(target);
  const fields: Array<'hundreds' | 'tens' | 'ones'> = ['hundreds', 'tens', 'ones'];
  const repairField = fields[randomBetween(0, 2)];
  const correctVal = { hundreds, tens, ones }[repairField];

  // Make a wrong value that's close but different
  let wrongVal: number;
  do {
    wrongVal = Math.max(0, correctVal + randomBetween(1, 3) * (Math.random() < 0.5 ? 1 : -1));
  } while (wrongVal === correctVal);

  const choices = wrongChoices(correctVal, 3, 0, repairField === 'hundreds' ? 9 : 9);

  return {
    type: 'repair',
    target, hundreds, tens, ones,
    repairField,
    repairCorrect: correctVal,
    repairWrong: wrongVal,
    choices,
    hint: `Look at the ${repairField}. Count how many groups of ${
      repairField === 'hundreds' ? '100' : repairField === 'tens' ? '10' : '1'
    } are in ${target}.`,
    skillId: 'math-place-value-repair',
  };
}

// ── Path question ────────────────────────────────────────────
export function makePathQuestion(difficulty: Difficulty): PlaceValueQuestion {
  let start: number, end: number;
  const steps: Array<{ op: '+' | '-'; val: 10 | 1 }> = [];

  if (difficulty === 'easy') {
    start = randomBetween(100, 150);
    const jump10 = randomBetween(1, 3);
    const jump1  = randomBetween(0, 5);
    end = start + jump10 * 10 + jump1;
    for (let i = 0; i < jump10; i++) steps.push({ op: '+', val: 10 });
    for (let i = 0; i < jump1; i++)  steps.push({ op: '+', val: 1 });
  } else if (difficulty === 'medium') {
    start = randomBetween(110, 180);
    const jump10 = randomBetween(2, 5);
    const jump1  = randomBetween(1, 9);
    end = start + jump10 * 10 + jump1;
    for (let i = 0; i < jump10; i++) steps.push({ op: '+', val: 10 });
    for (let i = 0; i < jump1; i++)  steps.push({ op: '+', val: 1 });
  } else {
    start = randomBetween(120, 250);
    const jump10 = randomBetween(3, 7);
    const jump1  = randomBetween(2, 9);
    end = start + jump10 * 10 + jump1;
    for (let i = 0; i < jump10; i++) steps.push({ op: '+', val: 10 });
    for (let i = 0; i < jump1; i++)  steps.push({ op: '+', val: 1 });
  }

  const { hundreds, tens, ones } = decompose(end);
  return {
    type: 'path',
    target: end, hundreds, tens, ones,
    pathStart: start,
    pathEnd: end,
    pathSteps: shuffle(steps),
    hint: `Start at ${start}. Use +10 and +1 to reach ${end}.`,
    skillId: 'math-place-value-path',
  };
}

// ── Challenge — mixed set of 3 questions ─────────────────────
export function makeChallenge(difficulty: Difficulty): PlaceValueQuestion[] {
  return [
    makeBuildQuestion(difficulty),
    makePathQuestion(difficulty),
    makeRepairQuestion(difficulty),
  ];
}

// ── XP rewards ───────────────────────────────────────────────
export const XP_CORRECT  = 15;
export const XP_HINT     = 8;
export const XP_COMPLETE = 50;
export const XP_BRIDGE   = 100;
